import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import type { DateRangeOption } from "@/modules/analytics/util";
import { useAnalyticsQueries } from "@/queries/analytics/queryOptions";
import { useTranslation } from "@/translations";
import type { NonEmptyArray } from "@/types/utility-types";

import { AdoptionAnalyticsDetails, type AdoptionAnalyticsDetailsProps } from "./AdoptionAnalyticsDetails";

export function AdoptionDetailPage(): React.ReactNode {
  const { t } = useTranslation();
  const { project } = useSessionUser();

  const dateRangeOptions = useMemo<NonEmptyArray<DateRangeOption>>(
    () => [
      {
        name: t("common.date-range.last-months", { count: 6 }),
        value: "last6Months",
      },
      {
        name: t("common.date-range.last-year"),
        value: "lastYear",
      },
    ],
    [t],
  );

  const [dateRange, setDateRange] = useState<DateRangeOption>(dateRangeOptions[0]);

  const queries = useAnalyticsQueries();
  const {
    data: reachData,
    isPending: isPendingReachData,
    error: reachError,
  } = useQuery({
    ...queries.reachDetails(dateRange.value),
  });

  const {
    data: adoptionData,
    isPending: isPendingAdoptionData,
    error: adoptionError,
  } = useQuery(queries.adoptionDetails(dateRange.value));

  const {
    data: vacantData,
    isPending: isPendingVacantData,
    error: vacantError,
  } = useQuery({
    ...queries.vacantAddresses(dateRange.value),
    enabled: project.type === "addressBased",
  });

  const error = adoptionError || vacantError || reachError;
  if (error) {
    return <ErrorPage error={error} />;
  }

  const contentProps: AdoptionAnalyticsDetailsProps = {
    adoption: adoptionData,
    vacant: vacantData?.data ?? [],
    reach: reachData?.data ?? [],
    isLoadingAdoptionData: isPendingAdoptionData,
    isLoadingVacantData: isPendingVacantData,
    isLoadingReachData: isPendingReachData,
    selectedDateRange: dateRange,
    dateRangeOptions,
    onDateRangeChange: setDateRange,
  };

  return <AdoptionAnalyticsDetails {...contentProps} />;
}
